import {
  type CellClassParams,
  type ColDef,
  type GridApi,
  type IAggFunc,
  type IAggFuncParams,
  type ValueGetterParams,
  AllCommunityModule,
  ClientSideRowModelModule,
  CsvExportModule,
} from 'ag-grid-community';
import { type ViewColumn } from 'common/types/viewColumn';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ValueRenderer } from './ValueRenderer';
import { headerClassFunction, pivotColumnLabelsClassFunction, isNullGrouping } from './helpers/ColDefHelpers';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { useAutoGroupAttributes } from './customHooks/useAutoGroupAttributes';
import { useHandleExport, useInitializeExport } from './customHooks/exportHooks';
import {
  ExcelExportModule,
  GroupFilterModule,
  PivotModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  TreeDataModule,
  CellSelectionModule,
  ClipboardModule,
  SideBarModule,
  ColumnsToolPanelModule,
  SetFilterModule,
  MultiFilterModule,
} from 'ag-grid-enterprise';
import eventBus from './helpers/EventBus';
import {
  generateCellClasses,
  generateExcelStyles,
  generateUrlValue,
  getCellClassRules,
} from './helpers/TableExportFormatting';
import { type Hierarchy, type HierarchyColumnConfig, type OrderConfig } from 'common/visualizations/vif';
import {
  type ExportTotalsInfo,
  type ExportData,
  type HeaderFormatStyle,
  type TableExportType,
} from './types';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import isUndefined from 'lodash/isUndefined';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import { getAgTableHeaderName } from './helpers/TableColumnFormatting';
import { getFieldName } from './helpers/getFieldName';
import { findColumnMetadata } from './customHooks/useColDefs';
import { NULL_GROUPING } from './Constants';
import { type CustomAgGridContext, type CustomColDefContext } from 'common/types/agGrid/context';
import { SoQLType } from 'common/types/soql';
import { createDefaultHierarchy } from 'common/authoring_workflow/helpers';
import { isPivotInHierarchy } from 'common/visualizations/helpers/PivotHelpers';
import { isSubtotalCell, isTotalCell } from './helpers/isTotalCell';
import { getCustomSubtotal } from './helpers/ExportCellTotalHelpers';
import { I18n } from 'common/i18n';
import { type ICellClassesParams } from './helpers/TableExportFormatting';
import {
  isTableAdvancedTotalingEnabled,
  isTableAdvancedTotalingForExportsEnabled,
  isTablePrintingImprovementFormattingEnabled,
} from 'common/visualizations/helpers/VifSelectors';
import { DIV_ZERO_SENTINEL } from 'common/authoring_workflow/components/modals/AdvancedTotalingModal/helpers/formulaHelpers';

export interface IExportGridParams {
  domain: string;
  datasetUid: string;
  columns: ViewColumn[];
  removedHierarchyColumnNames: Set<string>;
  hierarchyColumns: HierarchyColumnConfig[];
  vifColumns: ViewColumn[];
  activeHierarchyId: string | undefined;
  agGridContext: any;
  headerFormat: HeaderFormatStyle;
  agGridReactAttributes: AgGridReactProps;
  hierarchyConfig?: Hierarchy;
  gridApi: GridApi | null;
  datasetName?: string;
  getExportData?: (selectedFiltered: boolean, exportType: TableExportType) => Promise<ExportData>;
  vizUid?: string;
  vifOrderConfig: OrderConfig[];
}

// the ag-grid aggregate on a footer row is not the sentinel (it's a sum/min/max/count of sentinels),
// so we have to consult the server-computed subtotal to decide whether the cell is a div-zero error.
export const isDivZeroSubtotalFooter = (
  params: CellClassParams,
  fieldName: string,
  exportTotalsInfo?: ExportTotalsInfo,
): boolean => {
  if (!params.node?.group || !isTableAdvancedTotalingEnabled() || !exportTotalsInfo) {
    return false;
  }
  return getCustomSubtotal(fieldName, exportTotalsInfo, params) == DIV_ZERO_SENTINEL;
};

export const buildCellClassesParams = (
  params: CellClassParams,
  fieldName: string,
  dataTypeName: SoQLType,
  exportTotalsInfo?: ExportTotalsInfo,
): ICellClassesParams => {
  const resolvedFieldName = getFieldName(params) ?? '';
  return {
    colFieldName: fieldName,
    colDataTypeName: dataTypeName,
    value: params.data?.[resolvedFieldName] ?? params.value,
    node: params.node,
    context: params.context,
    colDef: params.colDef,
    isDivZeroSubtotal: isDivZeroSubtotalFooter(params, fieldName, exportTotalsInfo),
    api: params.api,
  };
};

export const buildDivZeroErrorRule = (
  fieldName: string,
  exportTotalsInfo?: ExportTotalsInfo,
): Record<string, (params: CellClassParams) => boolean> => {
  return {
    'div-zero-error': (params: CellClassParams) =>
      isDivZeroSubtotalFooter(params, fieldName, exportTotalsInfo),
  };
};

// exportMinFunc mimics the server-side behavior of MIN(string).
// By default, Ag-Grid does not provide min aggregation for strings.
// This also applies to exportMaxFunc
export const exportMinFunc = (params: IAggFuncParams) => {
  const { values } = params;
  return minBy(values, (str) => str);
};

export const exportMaxFunc = (params: IAggFuncParams) => {
  const { values } = params;
  return maxBy(values, (str) => str);
};

export const exportCountFunc = (params: IAggFuncParams) => {
  const { values } = params;

  // do not count undefined, empty string, and NaN values
  const acceptedValues = values.filter(
    (val) =>
      (typeof val == 'string' && val) || (typeof val == 'number' && !isNaN(val)) || typeof val == 'boolean',
  );
  return acceptedValues.length;
};

export const exportCountDistinctFunc = (params: IAggFuncParams) => {
  const { values } = params;

  // do not count undefined, empty string, and NaN values
  const acceptedValues = values.filter(
    (val) =>
      (typeof val == 'string' && val) || (typeof val == 'number' && !isNaN(val)) || typeof val == 'boolean',
  );
  return new Set(acceptedValues).size;
};

const ExportGrid = (props: IExportGridParams) => {
  const {
    domain,
    datasetUid,
    hierarchyConfig,
    columns,
    removedHierarchyColumnNames,
    hierarchyColumns,
    vifColumns,
    datasetName,
    getExportData,
    activeHierarchyId,
    vizUid,
    agGridContext,
    gridApi,
    headerFormat,
    agGridReactAttributes,
  } = props;
  const gridRef = useRef<AgGridReact<any>>(null);
  const [isExportTableRendered, setIsExportTableRendered] = useState<boolean>(false);
  const [rowsToBeExported, setRowsToBeExported] = useState<unknown[] | undefined>(undefined);
  const [exportTotalsInfo, setExportTotalsInfo] = useState<ExportTotalsInfo | undefined>(undefined);
  const [currentExcelStyles, setCurrentExcelStyles] = useState([] as any);

  const initializeExport = useInitializeExport({
    viewColumns: vifColumns,
    getExportData: getExportData,
    setIsExportTableRendered: setIsExportTableRendered,
    setRowsToBeExported: setRowsToBeExported,
    setExportTotalsInfo: setExportTotalsInfo,
  });

  const handleExport = useHandleExport({
    gridRef,
    isExportTableRendered,
    rowsToBeExported,
    datasetName,
    setIsExportTableRendered,
    setRowsToBeExported,
    exportTotalsInfo,
    setExportTotalsInfo,
    viewColumns: vifColumns,
    columnFormats: agGridContext.current?.columnFormats ?? {},
    domain,
    datasetUid,
  });

  useEffect(() => {
    eventBus.on(`exportGridData-${vizUid}`, initializeExport);
    return () => {
      eventBus.remove(`exportGridData-${vizUid}`, initializeExport);
    };
  }, [initializeExport, vizUid]);

  useEffect(() => {
    if (gridApi && gridRef) {
      const excelStyles = generateExcelStyles({
        columnFormats: agGridContext.current.columnFormats,
        rowStripeStyle: agGridContext.current.currentRowStripeStyle,
        columnMetadata: agGridContext.current.columnMetadata,
        headerFormat: agGridContext.current.headerFormat,
      });
      setCurrentExcelStyles(excelStyles);
    }
  }, [
    agGridContext.current.columnFormats,
    agGridContext.current.currentRowStripeStyle,
    gridApi,
    headerFormat,
  ]);

  const clientSideModules = [
    ClientSideRowModelModule,
    AllCommunityModule,
    ExcelExportModule,
    CsvExportModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    GroupFilterModule,
    TreeDataModule,
    PivotModule,
    CellSelectionModule,
    ClipboardModule,
    SideBarModule,
    ColumnsToolPanelModule,
    SetFilterModule,
    MultiFilterModule,
  ];

  const exportColDefs = () => {
    const localHierarchyConfig = (hierarchyConfigIdx: number): HierarchyColumnConfig =>
      hierarchyColumns[hierarchyConfigIdx];

    const defs: ColDef[] = columns.map((column: ViewColumn) => {
      // to ag-grid, `number` will define the ordering of the group columns;
      // `null` means to clear the existing value (but can only be used if there _is_ an existing value)
      // `undefined` means that this isn't a row group (it also means don't change the existing value, if present)
      let rowGroupIndex: number | null | undefined;
      const shouldResetRowIndex = removedHierarchyColumnNames?.has(column.fieldName);
      const hierarchyConfigIdx = hierarchyColumns.findIndex(
        ({ columnName }) => columnName === column.fieldName,
      );

      const hierarchyAggregation = localHierarchyConfig(hierarchyConfigIdx)?.aggregation ?? null;
      const isColumnGrouping = localHierarchyConfig(hierarchyConfigIdx)?.isGrouping ?? false;
      const pivotAllowed = isPivotInHierarchy(hierarchyConfig!);
      const isColumnPivoting = localHierarchyConfig(hierarchyConfigIdx)?.isPivoting ?? false;
      const isColumnHidden =
        (hierarchyConfig ? localHierarchyConfig(hierarchyConfigIdx)?.hidden : column.hide) ?? false;
      const columnFormat = agGridContext.current?.columnFormats[column.fieldName];
      const viewColumnMetadata = findColumnMetadata(column, agGridContext.current?.columnMetadata) ?? column;
      const displayName = getAgTableHeaderName(columnFormat, viewColumnMetadata);

      if (shouldResetRowIndex) {
        rowGroupIndex = null;
      } else if (isColumnGrouping) {
        rowGroupIndex = hierarchyConfigIdx;
      }

      let pivotIndex;
      let pivot = false;
      if (pivotAllowed && isColumnPivoting) {
        pivot = true;
        // Pivot columns are always stored after row group columns in the hierarchy config
        const numberOfRowGroups = hierarchyColumns.filter((col) => col.isGrouping === true).length;
        pivotIndex = hierarchyConfigIdx - numberOfRowGroups;
      }

      const divZeroErrorRule = buildDivZeroErrorRule(column.fieldName, exportTotalsInfo);

      return {
        field: column.fieldName,
        context: {
          pivotValueColumn: column.fieldName,
          pivotAllowed: pivotAllowed,
          exporting: true,
          aggFunc: hierarchyAggregation,
        } satisfies CustomColDefContext,
        valueGetter: (params: ValueGetterParams) => {
          if (!params.data) {
            return;
          }
          const field = params.colDef.field ?? '';
          const value = params.data[field];
          const ctx = params.context as CustomAgGridContext;

          const { groupedColumns } = ctx;

          // Force rendering of the total label on the pinned row and first column when no aggregation is defined
          const firstColumnFieldname = params.api.getDisplayedCenterColumns()[0]?.getColId();
          if (isTotalCell(params) && firstColumnFieldname == field && !hierarchyAggregation) {
            if (isTableAdvancedTotalingForExportsEnabled()) {
              const customTotalLabel = hierarchyConfig?.customLabels?.totals?.trim();
              if (customTotalLabel) {
                return customTotalLabel;
              }
            }
            return I18n.t('shared.visualizations.charts.table.total');
          }

          if (isNullGrouping(groupedColumns, value, field)) {
            return NULL_GROUPING;
          }

          if (column.dataTypeName == 'number' && !isEmpty(value)) {
            return parseFloat(value);
          }

          const isDateValue =
            !isUndefined(value) &&
            column.dataTypeName === SoQLType.SoQLFloatingTimestampT &&
            !isTotalCell(params);
          if (isDateValue) {
            return value.split('.')[0];
          }

          // As of September 2024, we do not use hyperlinks for URLs,
          // if we are to use hyperlinks in the future just edit this condition
          // reference: https://www.ag-grid.com/react-data-grid/excel-export-hyperlinks/
          if (column.dataTypeName == 'url') {
            return generateUrlValue(value);
          }

          return value;
        },
        aggFunc: hierarchyAggregation,
        rowGroupIndex,
        pivot,
        pivotIndex,
        hide: isColumnHidden || isColumnGrouping,
        cellRendererParams: {
          datasetUid,
          domain,
          wrapWithDivElement: true,
          forExport: true,
        },
        cellRenderer: ValueRenderer,
        cellClassRules: {
          'left-align': (params) => getCellClassRules(params, 'left'),
          'center-align': (params) => getCellClassRules(params, 'center'),
          'right-align': (params) => getCellClassRules(params, 'right'),
          'total-row': isTotalCell,
          'subtotal-row': isSubtotalCell,
          ...divZeroErrorRule,
        },
        cellClass: (params: CellClassParams) => {
          // params.data contains the true/unformatted value
          // whereas params.value contains the formatted value from the renderer (exportHooks - processCellCallback).
          return generateCellClasses(
            buildCellClassesParams(params, column.fieldName, column.dataTypeName, exportTotalsInfo),
          );
        },
        headerClass: headerClassFunction,
        headerName: displayName,
        width: column.width,
      } as ColDef;
    });
    return defs;
  };

  const exportAutoGroupAttributes = useAutoGroupAttributes({
    datasetUid,
    domain,
    headerFormat,
    isIndented: false,
    singleAutoColumnWidth: hierarchyConfig?.singleAutoColumnWidth,
    shouldGenerateExportCellClasses: true,
    vifOrderConfig: [],
    columnFormats: {},
    hierarchyConfig: hierarchyConfig ?? createDefaultHierarchy(),
  });

  const aggFuncs = useMemo<{
    [key: string]: IAggFunc;
  }>(() => {
    return {
      max: exportMaxFunc,
      min: exportMinFunc,
      count: exportCountFunc,
      countDistinct: exportCountDistinctFunc,
    };
  }, []);

  /**
   * Conditionally render a ClientSide AgGrid implementation for XLSX exports if and only if
   * the user has selected the XLSX option in the Export Modal component and clicked on the
   * `Download` button.
   *
   * We have to render another table because AgGrid only exports data that is loaded on the
   * web client. This is not possible with a ServerSide implementation of AgGrid, so we have
   * to render the ClientSide table in the method below that has most of the same attributes
   * as our ServerSide table.
   *
   * see https://socrata.atlassian.net/wiki/spaces/PD/pages/2911895579/EN-65533+Excel+CSV+Export+with+formatting+for+AG+Grid
   */
  return isExportTableRendered ? (
    <div className={`table-export-${vizUid}`} style={{ display: 'none' }}>
      <AgGridReact
        {...agGridReactAttributes}
        columnDefs={exportColDefs()}
        ref={gridRef}
        rowData={rowsToBeExported}
        onRowDataUpdated={activeHierarchyId == hierarchyConfig?.id ? handleExport : undefined}
        rowModelType="clientSide"
        groupDefaultExpanded={-1}
        pivotDefaultExpanded={-1}
        modules={clientSideModules}
        onGridReady={(params) => {
          params.api.addAggFuncs({ max: exportMaxFunc, min: exportMinFunc, count: exportCountFunc });
        }}
        onModelUpdated={(params) => {
          // redraw pinned bottom rows specifically for total row styling
          if (isTablePrintingImprovementFormattingEnabled()) {
            const pinnedBottomRow = params.api.getPinnedBottomRow(0);
            if (pinnedBottomRow) {
              params.api.redrawRows({ rowNodes: [pinnedBottomRow] });
            }
          }
        }}
        getRowId={undefined}
        isServerSideGroupOpenByDefault={undefined}
        serverSideDatasource={undefined}
        excelStyles={currentExcelStyles}
        groupDisplayType={'multipleColumns'}
        autoGroupColumnDef={exportAutoGroupAttributes}
        processPivotResultColGroupDef={(colGroupDef) => {
          colGroupDef.headerClass = pivotColumnLabelsClassFunction;
        }}
        aggFuncs={aggFuncs}
        onFirstDataRendered={undefined}
      />
    </div>
  ) : null;
};

export default ExportGrid;
